import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

import type { OverrideStructuredChange } from "@automatry/schemas";

import { buildScopedOverridePreviewAsync } from "../app/lazyModules";
import type {
  CarringtonEngineFixture,
  ScopedOverridePreviewResult
} from "../impactPreview";
import type { WorkspaceItem } from "../viewmodel/solutionViewAdapter";
import {
  cx,
  itemTone,
  ProvenanceAffordance,
  riskTone,
  sourceDisplayLabel,
  sourceTone
} from "./shared";
import { Term } from "./Glossary";

export interface ScopedOverrideAction {
  id: string;
  label: string;
  description: string;
  intent: string;
  reason: string;
  structuredChange: OverrideStructuredChange;
}

export const itemTargetCollection = (
  item: WorkspaceItem
): "controllers" | "devices" | "infrastructure" | null => {
  if (item.targetCollection) {
    return item.targetCollection;
  }

  if (item.category === "Controllers") {
    return "controllers";
  }

  if (item.id.startsWith("workspace-device-")) {
    return "devices";
  }

  if (item.id.startsWith("workspace-infrastructure-")) {
    return "infrastructure";
  }

  return null;
};

export const itemSelector = (
  item: WorkspaceItem
): { productPartNo?: string; role?: string; itemKind?: string } => {
  const selector: { productPartNo?: string; role?: string; itemKind?: string } = {};
  const targetCollection = itemTargetCollection(item);

  if (item.partNo) {
    selector.productPartNo = item.partNo;
  }

  if (targetCollection === "devices" && item.selectorRole) {
    selector.role = item.selectorRole;
  }

  if (targetCollection === "infrastructure" && item.selectorItemKind) {
    selector.itemKind = item.selectorItemKind;
  }

  return selector;
};

export const itemPlainProvenance = (item: WorkspaceItem): string =>
  item.category === "Controllers"
    ? "Selected to meet the required controls capacity, then checked against the supplied product catalogue."
    : item.ruleTraceRefs.length > 0
      ? "Derived from the saved project inputs and checked against the attached design rules."
      : item.sourceRefCount > 0
        ? "Derived from the saved project inputs and attached source evidence."
        : "Derived from the saved specification data.";

export const scopedOverrideActionsForItem = (
  item: WorkspaceItem
): ScopedOverrideAction[] => {
  const targetCollection = itemTargetCollection(item);
  const selector = itemSelector(item);
  const actions: ScopedOverrideAction[] = [];
  const canAdjustQuantity =
    targetCollection !== null &&
    item.partNo !== undefined &&
    (targetCollection !== "devices" || item.selectorRole !== undefined);

  if (canAdjustQuantity) {
    actions.push({
      id: `${item.id}-qty-plus-one`,
      label: "Qty +1",
      description: "Increase this generated item by one and re-run validation.",
      intent: `Increase ${item.name} quantity by one for this apartment type.`,
      reason: `Estimator preview: increase ${item.name} by one as a scoped override.`,
      structuredChange: {
        kind: "adjust_quantity",
        targetCollection,
        selector,
        delta: 1
      }
    });
    actions.push({
      id: `${item.id}-qty-minus-one`,
      label: item.category === "Controllers" ? "Qty -1 capacity check" : "Qty -1",
      description: "Reduce this generated item by one and let engine guards decide.",
      intent: `Reduce ${item.name} quantity by one for this apartment type.`,
      reason: `Estimator preview: reduce ${item.name} by one as a scoped override.`,
      structuredChange: {
        kind: "adjust_quantity",
        targetCollection,
        selector,
        delta: -1
      }
    });

    if (targetCollection === "controllers" && item.quantity > 1) {
      actions.push({
        id: `${item.id}-remove-controller-plan`,
        label: "Remove controller plan",
        description:
          "Remove the matching controller allocation and let the point ledger reject unsafe capacity.",
        intent: `Remove the generated ${item.name} controller allocation for this apartment type.`,
        reason:
          "Estimator preview: prove the scoped override guard rejects an unsafe controller removal.",
        structuredChange: {
          kind: "adjust_quantity",
          targetCollection,
          selector,
          delta: -item.quantity
        }
      });
    }
  }

  if (item.partNo === "TP-UI-TS-4" || item.role.toLowerCase().includes("ts4")) {
    actions.push({
      id: `${item.id}-strategy-rdu`,
      label: "Use RDU strategy",
      description: "Switch local UI strategy to RDU and re-derive mappings.",
      intent: "Substitute Type A local TS-4 screens to RDU direct-wired screens.",
      reason: "Estimator preview: client requested RDU room displays.",
      structuredChange: {
        kind: "set_strategy_input",
        input: "ui_screen_kind",
        value: "rdu"
      }
    });
  }

  actions.push({
    id: `${item.id}-add-io-module`,
    label: "Add IO/COM-8R",
    description: "Add one reviewed IO module for spare capacity.",
    intent: "Add one IO/COM-8R module for this apartment type.",
    reason: "Estimator preview: allow one additional commercial spare IO module.",
    structuredChange: {
      operation: "add_io_module",
      productPartNo: "IO/COM-8R",
      quantity: 1
    }
  });

  return actions;
};

const previewTone = {
  passed: "border-[#D9DED5] bg-[#EAF1E6] text-[#4E7644]",
  warning: "border-[#E9D3AD] bg-[#F6EEDD] text-[#8A6A2E]",
  blocked: "border-[#E9C7C0] bg-[#F6E7E4] text-[#BC4A3C]"
} as const;

const drawerFocusableSelector = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

const drawerFocusableElements = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(drawerFocusableSelector)).filter(
    (element) => element.getAttribute("aria-hidden") !== "true"
  );

export function ScopedOverridePanel({
  actions,
  selectedActionId,
  engineReady,
  engineLoading,
  engineFixtureError,
  preview,
  previewLoading,
  previewError,
  onSelectAction,
  onPreview,
  onClear
}: {
  actions: readonly ScopedOverrideAction[];
  selectedActionId: string;
  engineReady: boolean;
  engineLoading: boolean;
  engineFixtureError: string | null;
  preview: ScopedOverridePreviewResult | null;
  previewLoading: boolean;
  previewError: string | null;
  onSelectAction: (id: string) => void;
  onPreview: () => void;
  onClear: () => void;
}) {
  const selectedAction = actions.find((action) => action.id === selectedActionId);
  const guardrail = preview?.view.guardrail;

  return (
    <section className="mt-[18px] rounded-[14px] border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
            Scoped override
          </div>
          <div className="mt-1 text-[12.5px] font-semibold text-[var(--muted)]">
            Base specification unchanged · preview checks the project rules
          </div>
        </div>
        {guardrail ? (
          <span
            className={cx(
              "rounded-full border px-2.5 py-1 text-[10.5px] font-bold",
              previewTone[guardrail.status]
            )}
          >
            {guardrail.status === "blocked"
              ? "Rejected"
              : guardrail.status === "warning"
                ? "Flagged"
                : "Accepted"}
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            onClick={() => onSelectAction(action.id)}
            className={cx(
              "rounded-[9px] border px-3 py-2 text-[11.5px] font-bold",
              selectedActionId === action.id
                ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]"
                : "border-[var(--line3)] text-[var(--muted)] hover:bg-[var(--chip)]"
            )}
          >
            {action.label}
          </button>
        ))}
      </div>

      {selectedAction ? (
        <p className="m-0 mt-3 text-[12.5px] leading-[1.45] text-[var(--muted2)]">
          {selectedAction.description}
        </p>
      ) : null}

      {engineFixtureError ? (
        <div className="mt-3 rounded-lg border border-[#E9C7C0] bg-[#F6E7E4] px-3 py-2 text-[12px] font-semibold text-[#BC4A3C]">
          Could not load preview data: {engineFixtureError}
        </div>
      ) : null}
      {engineLoading ? (
        <div className="mt-3 rounded-lg border border-[var(--line)] bg-[var(--chip)] px-3 py-2 text-[12px] font-semibold text-[var(--muted)]">
          Loading preview data...
        </div>
      ) : null}
      {previewError ? (
        <div className="mt-3 rounded-lg border border-[#E9C7C0] bg-[#F6E7E4] px-3 py-2 text-[12px] font-semibold text-[#BC4A3C]">
          Preview failed: {previewError}
        </div>
      ) : null}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onPreview}
          disabled={!engineReady || previewLoading || actions.length === 0}
          className={cx(
            "flex-1 rounded-[10px] px-3 py-2.5 text-[12.5px] font-bold",
            engineReady && !previewLoading && actions.length > 0
              ? "bg-[var(--ink)] text-[var(--onSolid)]"
              : "bg-[var(--line2)] text-[var(--faint)]"
          )}
        >
          {previewLoading
            ? "Preparing preview..."
            : engineLoading
              ? "Loading fixture..."
              : "Preview before/after"}
        </button>
        {preview ? (
          <button
            type="button"
            onClick={onClear}
            className="rounded-[10px] border border-[var(--line3)] px-3 py-2.5 text-[12.5px] font-bold text-[var(--muted)]"
          >
            Clear
          </button>
        ) : null}
      </div>

      {preview ? (
        <div className="mt-4 space-y-3">
          <div className="rounded-[11px] border border-[var(--line3)] bg-[var(--raised2)] px-3 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[12.5px] font-extrabold text-[var(--ink)]">
                Before/after impact
              </div>
              <div className="text-[11px] font-bold text-[var(--muted)]">
                {preview.baseSnapshot.id} → {preview.overrideSnapshot.id}
              </div>
            </div>
            <p className="m-0 mt-1.5 text-[12px] leading-[1.45] text-[var(--muted)]">
              {preview.view.summary}
            </p>
          </div>

          {preview.productDeltas.length > 0 ? (
            <div className="overflow-hidden rounded-[11px] border border-[var(--line)]">
              {preview.productDeltas.map((delta) => (
                <div
                  key={delta.productRef}
                  className="grid grid-cols-[1fr_auto] gap-3 border-t border-[var(--line2)] bg-[var(--surface)] px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-extrabold text-[var(--ink)]">
                      {delta.productRef}
                    </div>
                    <div className="truncate text-[11px] text-[var(--faint)]">
                      {delta.label}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[12px] font-extrabold text-[var(--ink)]">
                      {delta.before} → {delta.after}
                    </div>
                    <div className="text-[11px] font-bold text-[var(--muted)]">
                      {delta.delta > 0 ? "+" : ""}
                      {delta.delta} · {delta.costDelta?.formatted ?? "No price"}
                    </div>
                  </div>
                </div>
              ))}
              <div className="border-t border-[var(--line)] bg-[var(--raised2)] px-3 py-2 text-right text-[12px] font-extrabold text-[var(--ink)]">
                Cost delta {preview.costDelta.formatted}
              </div>
            </div>
          ) : null}

          {preview.capacityDeltas.length > 0 ? (
            <div className="rounded-[11px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5">
              <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                Capacity
              </div>
              <div className="grid gap-1.5">
                {preview.capacityDeltas.map((delta) => (
                  <div
                    key={delta.pointKind}
                    className="flex items-center justify-between gap-3 text-[11.5px]"
                  >
                    <span className="font-extrabold uppercase text-[var(--muted)]">
                      {delta.pointKind}
                    </span>
                    <span className="text-right font-semibold text-[var(--ink2)]">
                      required {delta.requiredBefore} → {delta.requiredAfter} ·
                      available {delta.availableBefore} → {delta.availableAfter} · spare{" "}
                      {delta.spareBefore} → {delta.spareAfter}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {guardrail ? (
            <div
              className={cx(
                "rounded-[11px] border px-3 py-2.5 text-[12px] font-semibold leading-[1.45]",
                previewTone[guardrail.status]
              )}
            >
              {guardrail.message}
              {guardrail.findingCodes.length > 0 ? (
                <div className="mt-1 text-[11px] font-bold">
                  {guardrail.findingCodes.join(", ")}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 text-[10.5px] font-bold text-[var(--faint)]">
            <span className="rounded-md bg-[var(--chip)] px-2 py-1">
              {preview.overrideDraft.id}
            </span>
            <span className="rounded-md bg-[var(--chip)] px-2 py-1">
              Base unchanged: {preview.baseUnchanged ? "yes" : "no"}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function DetailDrawer({
  item,
  apartmentTypeId,
  engineFixture,
  engineFixtureLoading,
  engineFixtureError,
  onClose
}: {
  item: WorkspaceItem;
  apartmentTypeId: string;
  engineFixture: CarringtonEngineFixture | null;
  engineFixtureLoading: boolean;
  engineFixtureError: string | null;
  onClose: () => void;
}) {
  const titleId = useId();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const actions = useMemo(() => scopedOverrideActionsForItem(item), [item]);
  const [selectedActionId, setSelectedActionId] = useState(actions[0]?.id ?? "");
  const [preview, setPreview] = useState<ScopedOverridePreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequestId = useRef(0);
  const selectedAction =
    actions.find((action) => action.id === selectedActionId) ?? actions[0] ?? null;
  const plainProvenance = itemPlainProvenance(item);

  useEffect(() => {
    previewRequestId.current += 1;
    setSelectedActionId(actions[0]?.id ?? "");
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError(null);
  }, [actions, item.id]);

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();

    return () => {
      const previousFocus = previousFocusRef.current;

      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [item.id]);

  const handleDrawerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();

      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    const drawer = drawerRef.current;

    if (!drawer) {
      return;
    }

    const focusable = drawerFocusableElements(drawer);

    if (focusable.length === 0) {
      event.preventDefault();
      drawer.focus();

      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!drawer.contains(active)) {
      event.preventDefault();
      first?.focus();

      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const handlePreview = async () => {
    if (!engineFixture || !selectedAction) {
      return;
    }

    const requestId = previewRequestId.current + 1;
    previewRequestId.current = requestId;

    try {
      setPreview(null);
      setPreviewLoading(true);
      setPreviewError(null);
      const nextPreview = await buildScopedOverridePreviewAsync({
        fixture: engineFixture,
        apartmentTypeId,
        actionId: selectedAction.id,
        label: selectedAction.label,
        intent: selectedAction.intent,
        reason: selectedAction.reason,
        structuredChange: selectedAction.structuredChange,
        scopeRefId: apartmentTypeId
      });

      if (previewRequestId.current === requestId) {
        setPreview(nextPreview);
      }
    } catch (error) {
      if (previewRequestId.current === requestId) {
        setPreview(null);
        setPreviewError(
          error instanceof Error ? error.message : "Unknown preview error"
        );
      }
    } finally {
      if (previewRequestId.current === requestId) {
        setPreviewLoading(false);
      }
    }
  };

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default bg-[rgba(35,33,30,.32)] backdrop-blur-[2px]"
      />
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleDrawerKeyDown}
        className="fixed right-0 top-0 z-[41] flex h-screen w-[430px] max-w-[92vw] animate-tcsDrawer flex-col overflow-hidden bg-[var(--rail)] shadow-[-24px_0_60px_-28px_rgba(35,33,30,.5)]"
      >
        <div className="flex-none border-b border-[var(--line)] px-[26px] pb-[18px] pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-[13px]">
              <span className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[11px] border border-[var(--line)] bg-[var(--surface)]">
                <span
                  className="h-[13px] w-[13px] rounded-[3px]"
                  style={{
                    background:
                      itemTone[item.category as keyof typeof itemTone] ??
                      "var(--muted2)"
                  }}
                />
              </span>
              <div>
                <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[var(--muted)]">
                  {item.category}
                </div>
                <div
                  id={titleId}
                  className="text-lg font-extrabold leading-[1.15] tracking-[-.02em]"
                >
                  {item.name}
                </div>
                <div className="mt-1 text-[12px] font-semibold leading-[1.35] text-[var(--muted2)]">
                  {item.role}
                </div>
              </div>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              aria-label="Close detail drawer"
              onClick={onClose}
              className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-[var(--line)] text-[15px] text-[var(--muted)]"
            >
              ×
            </button>
          </div>
          <div className="mt-[14px] flex flex-wrap gap-[7px]">
            <span
              className={cx(
                "rounded-full px-[11px] py-1 text-[11px] font-bold",
                sourceTone(item.source)
              )}
            >
              {sourceDisplayLabel(item.source)}
            </span>
            <span
              className={cx(
                "rounded-full border px-[11px] py-1 text-[11px] font-bold",
                riskTone[item.riskBadge.level]
              )}
            >
              {item.riskBadge.label}
            </span>
            <span className="rounded-full bg-[var(--chip)] px-[11px] py-1 text-[11px] font-bold text-[var(--muted)]">
              {item.quantity} {item.unit}
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-[26px] py-5">
          <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
            Why it is here
          </div>
          <p className="m-0 mb-[18px] text-sm leading-[1.55] text-[var(--ink2)]">
            {item.why}
          </p>
          <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
            How it is wired / used
          </div>
          <p className="m-0 mb-1.5 text-sm leading-[1.5] text-[var(--ink2)]">
            {plainProvenance}
          </p>
          <div className="mb-[18px] text-xs font-semibold text-[var(--muted2)]">
            {item.detailLines.join(" · ")}
          </div>
          {item.isGrouped ? (
            <div className="mb-[18px] rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
                  Generated split
                </div>
                <div className="text-[11px] font-bold text-[var(--muted2)]">
                  {item.instanceCount} source row
                  {item.instanceCount === 1 ? "" : "s"} · {item.quantity} total
                </div>
              </div>
              <div className="mt-3 grid gap-2">
                {item.subItems.map((subItem) => (
                  <div
                    key={subItem.id}
                    className="rounded-[10px] border border-[var(--line2)] bg-[var(--raised2)] px-3 py-2.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-extrabold leading-[1.25] text-[var(--ink)]">
                          {subItem.role}
                        </div>
                        <div className="mt-1 text-[11px] font-semibold leading-[1.35] text-[var(--muted2)]">
                          {subItem.detailLines.slice(0, 2).join(" · ")}
                        </div>
                      </div>
                      <div className="flex flex-none items-baseline gap-1 rounded-lg bg-[var(--surface)] px-2.5 py-1">
                        <span className="text-[16px] font-extrabold">
                          {subItem.quantity}
                        </span>
                        <span className="text-[10.5px] font-bold text-[var(--faint)]">
                          {subItem.unit}
                        </span>
                      </div>
                    </div>
                    {item.subItems.length > 1 ? (
                      <p className="m-0 mt-2 text-[11.5px] leading-[1.45] text-[var(--muted)]">
                        {subItem.how}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="mb-[18px]">
            <ProvenanceAffordance
              why={item.why}
              where={`Where: ${plainProvenance}`}
              rawData={{ implementationProvenance: item.how }}
              defaulted={item.why.includes(
                "Default - no specific requirement found in the documents"
              )}
              evidence={{
                sourceRefs: item.sourceRefs,
                sourceRefCount: item.sourceRefCount,
                ruleTraceRefs: item.ruleTraceRefs
              }}
            />
          </div>
          {item.points.length > 0 ? (
            <>
              <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
                <Term name="point">Points</Term> it uses
              </div>
              <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface)]">
                {item.points.map((point) => (
                  <div
                    key={point.kind}
                    className="flex items-center gap-3 border-t border-[var(--line2)] px-[14px] py-[9px]"
                  >
                    <span className="min-w-[38px] rounded-md bg-[var(--line2)] px-2 py-0.5 text-center text-[10.5px] font-extrabold tracking-[.03em] text-[var(--muted)]">
                      {point.kind}
                    </span>
                    <span className="text-[13.5px] font-semibold text-[var(--ink2)]">
                      {point.required} required · {point.spare} spare
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          {item.riskBadge.level !== "none" ? (
            <div className="mt-[18px] rounded-xl border border-[#E9C7C0] border-l-[3px] border-l-[#BC4A3C] bg-[#FBF1EF] px-4 py-[14px]">
              <div className="mb-1 text-[10.5px] font-bold uppercase tracking-[.06em] text-[#BC4A3C]">
                Linked risk
              </div>
              <p className="m-0 text-[13px] leading-[1.5] text-[var(--ink2)]">
                {item.riskBadge.notes}
              </p>
            </div>
          ) : null}
          <ScopedOverridePanel
            actions={actions}
            selectedActionId={selectedAction?.id ?? ""}
            engineReady={engineFixture !== null}
            engineLoading={engineFixtureLoading}
            engineFixtureError={engineFixtureError}
            preview={preview}
            previewLoading={previewLoading}
            previewError={previewError}
            onSelectAction={(id) => {
              previewRequestId.current += 1;
              setSelectedActionId(id);
              setPreview(null);
              setPreviewLoading(false);
              setPreviewError(null);
            }}
            onPreview={handlePreview}
            onClear={() => {
              previewRequestId.current += 1;
              setPreview(null);
              setPreviewLoading(false);
              setPreviewError(null);
            }}
          />
        </div>
        <div className="flex flex-none gap-2.5 border-t border-[var(--line)] bg-[var(--surface)] px-[26px] py-[14px]">
          <button
            type="button"
            onClick={handlePreview}
            disabled={engineFixture === null || previewLoading}
            className={cx(
              "flex-1 rounded-[10px] p-[11px] text-center text-[13px] font-bold",
              engineFixture !== null && !previewLoading
                ? "bg-[var(--ink)] text-[var(--onSolid)]"
                : "bg-[var(--line2)] text-[var(--faint)]"
            )}
          >
            {previewLoading
              ? "Preparing preview..."
              : engineFixtureLoading
                ? "Loading fixture..."
                : "Preview scoped override"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-[var(--line3)] px-4 py-[11px] text-[13px] font-bold text-[var(--muted)]"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}
